/**
 * Shared formatting utilities for consistent date, currency, and text formatting.
 * 
 * Import these functions instead of defining them inline in components.
 */

/**
 * Format a date string to a localized date format.
 * 
 * @param {string|Date} dateString - The date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    return date.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format a date string to include time.
 * 
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Invalid date';
  }
};

/**
 * Format a date as a relative time string (e.g., "2 hours ago").
 * 
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    // Treat naive timestamps (no timezone) as UTC
    let normalized = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      normalized = dateString + 'Z';
    }
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return formatDate(dateString);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Unknown';
  }
};

/**
 * Currency symbol lookup for fallback formatting.
 * @type {Object<string, string>}
 */
const CURRENCY_SYMBOLS = { USD: '$', INR: '₹' };

/**
 * Get the locale best suited for a given currency code.
 * @param {string} currency - ISO 4217 currency code
 * @returns {string} - BCP 47 locale tag
 */
const localeForCurrency = (currency) => {
  if (currency === 'INR') return 'en-IN';
  return 'en-US';
};

/**
 * Cache for Intl.NumberFormat instances.
 * Avoids creating new formatters on every call, reducing GC pressure
 * in list views that render 50+ currency values.
 */
const numberFormatCache = new Map();

/**
 * Get or create a cached Intl.NumberFormat instance.
 * @param {string} locale - BCP 47 locale tag
 * @param {Object} options - Intl.NumberFormat options
 * @returns {Intl.NumberFormat}
 */
const getCachedFormatter = (locale, options) => {
  const key = `${locale}:${JSON.stringify(options)}`;
  if (!numberFormatCache.has(key)) {
    numberFormatCache.set(key, new Intl.NumberFormat(locale, options));
  }
  return numberFormatCache.get(key);
};

/**
 * Format a number as currency. Whole-dollar by default — MRR / ARR / invoice
 * totals are displayed without fractional digits across the app.
 *
 * @param {number} amount - The amount to format
 * @param {string} currency - ISO 4217 currency code (default: USD)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  const sym = CURRENCY_SYMBOLS[currency] || '$';
  if (amount === null || amount === undefined) return `${sym}0`;

  try {
    const opts = { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return getCachedFormatter(localeForCurrency(currency), opts).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${sym}${Math.round(Number(amount))}`;
  }
};

/**
 * Format a large number with abbreviations (e.g., 1.2K, 3.4M).
 * 
 * @param {number} num - The number to format
 * @returns {string} - Abbreviated number string
 */
export const formatCompactNumber = (num) => {
  if (num === null || num === undefined) return '0';
  
  try {
    return getCachedFormatter('en-US', { notation: 'compact', compactDisplay: 'short' }).format(num);
  } catch (error) {
    return num.toString();
  }
};

/**
 * Format a percentage value.
 * 
 * @param {number} value - The value (0-100 or 0-1)
 * @param {boolean} isDecimal - Whether the value is already decimal (0-1)
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted percentage string
 */
export const formatPercent = (value, isDecimal = false, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  
  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Format an optional percentage without treating a valid zero as missing.
 *
 * @param {number|string|null|undefined} value - Percentage in the 0-100 scale
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted percentage or N/A when unavailable
 */
export const formatOptionalPercent = (value, decimals = 0) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const percentage = Number(value);
  return Number.isFinite(percentage) ? `${percentage.toFixed(decimals)}%` : 'N/A';
};

/**
 * Truncate text to a specified length with ellipsis.
 * 
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format a phone number to a readable format.
 * 
 * @param {string} phone - The phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  return phone;
};

/**
 * Format currency with compact notation (e.g., $1.2M, ₹3.4K).
 * Useful for dashboard cards and charts.
 *
 * @param {number} amount - The amount to format
 * @param {string} currency - ISO 4217 currency code (default: USD)
 * @returns {string} - Compact currency string
 */
export const formatCurrencyCompact = (amount, currency = 'USD') => {
  const sym = CURRENCY_SYMBOLS[currency] || '$';
  if (amount === null || amount === undefined) return `${sym}0`;

  if (Math.abs(amount) >= 1000000) {
    return `${sym}${(amount / 1000000).toFixed(1)}M`;
  } else if (Math.abs(amount) >= 1000) {
    return `${sym}${(amount / 1000).toFixed(1)}K`;
  }
  return formatCurrency(amount, currency);
};

/**
 * Format currency with zero fraction digits (e.g., $1,234 or ₹1,234).
 * Commonly used in tables and lists.
 *
 * @param {number} amount - The amount to format
 * @param {string} currency - ISO 4217 currency code (default: USD)
 * @returns {string} - Whole currency string
 */
export const formatCurrencyWhole = (amount, currency = 'USD') => {
  const sym = CURRENCY_SYMBOLS[currency] || '$';
  if (amount === null || amount === undefined) return `${sym}0`;

  try {
    const opts = { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return getCachedFormatter(localeForCurrency(currency), opts).format(amount);
  } catch (error) {
    return `${sym}${Math.round(Number(amount))}`;
  }
};

/**
 * Format a date as ISO date string (YYYY-MM-DD).
 *
 * @param {string|Date} date - The date to format
 * @returns {string} - ISO date string
 */
export const formatDateISO = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

/**
 * Calculate percentage change between two values.
 *
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {number} - Percentage change
 */
export const calcPercentChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Safely coerce a value (string or object) to a displayable string.
 * Handles LLM responses that non-deterministically return objects
 * (e.g. {action, owner}) instead of plain strings for array fields.
 *
 * @param {string|object|*} val - The value to coerce
 * @returns {string} - A renderable string
 */
export const toDisplayString = (val) => {
  if (typeof val === 'string') return val;
  if (val && typeof val === 'object') {
    // Handle {action, owner} pattern from LLM
    if (val.action) return val.owner ? `${val.action} (Owner: ${val.owner})` : val.action;
    // Handle {topic, notes} pattern from LLM
    if (val.topic) return val.notes ? `${val.topic}: ${val.notes}` : val.topic;
    // Handle {title, description} pattern
    if (val.title) return val.description ? `${val.title} — ${val.description}` : val.title;
    // Generic fallback: join all non-empty values
    const values = Object.values(val).filter(v => v != null && v !== '');
    if (values.length > 0) return values.join(' — ');
  }
  return String(val ?? '');
};

/**
 * Returns a date one week from now as a YYYY-MM-DD string (for date inputs).
 */
export const getDefaultDueDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
};

/**
 * Returns a datetime one week from now as a YYYY-MM-DDTHH:mm string (for datetime-local inputs).
 */
export const getDefaultDueDateTimeStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 16);
};

/**
 * Returns a date one week from now split into { date: 'YYYY-MM-DD', time: 'HH:mm' }.
 */
export const getDefaultMeetingDateParts = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return {
    date: d.toISOString().split('T')[0],
    time: d.toTimeString().slice(0, 5),
  };
};

/**
 * Get the browser's IANA timezone (e.g. "Asia/Kolkata", "America/New_York").
 */
export const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Convert a local date/time string to a UTC ISO string.
 * Handles date-only ("2026-03-14"), datetime-local ("2026-03-14T09:00"),
 * and full ISO strings.
 *
 * @param {string} localDateStr - Local date/time string from an input
 * @returns {string} UTC ISO string with Z suffix
 */
export const localToUTC = (localDateStr) => {
  if (!localDateStr) return null;
  const d = new Date(localDateStr);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyWhole,
  formatCompactNumber,
  formatPercent,
  formatDateISO,
  truncateText,
  formatPhoneNumber,
  calcPercentChange,
  toDisplayString,
  getDefaultDueDateStr,
  getDefaultDueDateTimeStr,
  getDefaultMeetingDateParts,
  getBrowserTimezone,
  localToUTC,
};
